import { Brain, Building2, Lightbulb, Target, TrendingUp } from 'lucide-react-native';
import type { ReactNode } from 'react';
import { ActivityIndicator } from 'react-native';
import { Box, Card, HStack, Text, VStack } from '../../../components/primitives';
import { useTranslation } from '../../../contexts/TranslationContext';
import { useTranslateOnFly } from '../../../hooks/useTranslateOnFly';
import { colors } from '../../../theme/colors';
import { withAlpha } from '../../../theme/colorUtils';
import type { AnalysisResult } from '../../../types/leads';

interface AnalysisCardProps {
  analysis: AnalysisResult;
}

function ScoreGauge({ score }: Readonly<{ score: number }>) {
  const getScoreColor = () => {
    if (score >= 70) return colors.status.success.main;
    if (score >= 40) return colors.status.warning.main;
    return colors.status.error.main;
  };

  const getScoreBg = () => {
    if (score >= 70) return colors.status.success.bg;
    if (score >= 40) return colors.status.warning.bg;
    return colors.status.error.bg;
  };

  const { t } = useTranslation();

  const getScoreLabel = () => {
    if (score >= 70) return t('leads.analysis.hot');
    if (score >= 40) return t('leads.analysis.warm');
    return t('leads.analysis.cold');
  };

  return (
    <Box backgroundColor={getScoreBg()} borderRadius="lg" padding="md" alignItems="center">
      <Text variant="h3" color={getScoreColor()} style={{ fontWeight: '800' }}>
        {score}
      </Text>
      <Text variant="caption" color={getScoreColor()} style={{ fontWeight: '600', marginTop: 2 }}>
        {getScoreLabel()} {t('leads.analysis.lead')}
      </Text>
      <Box
        backgroundColor={colors.background.secondary}
        borderRadius="full"
        style={{ width: '100%', height: 4, marginTop: 8 }}
      >
        <Box
          backgroundColor={getScoreColor()}
          borderRadius="full"
          style={{ width: `${score}%`, height: 4 }}
        />
      </Box>
    </Box>
  );
}

// The tint used to be a ring. A ring here is a border inside a card, and
// thirty-two-pixel ringed squares stacked down a column read as more boxes.
function IconWell({ icon, ring }: Readonly<{ icon: ReactNode; ring: string }>) {
  return (
    <Box
      width={32}
      height={32}
      borderRadius="md"
      backgroundColor={withAlpha(ring, 0.14)}
      alignItems="center"
      justifyContent="center"
    >
      {icon}
    </Box>
  );
}

function InfoRow({
  icon,
  ring,
  label,
  value,
}: Readonly<{ icon: ReactNode; ring: string; label: string; value: string }>) {
  return (
    <HStack spacing="sm" align="center">
      <IconWell icon={icon} ring={ring} />
      <Text variant="label" color={colors.text.tertiary} style={{ width: 70 }} numberOfLines={1}>
        {label}
      </Text>
      <Text variant="bodySmall" color={colors.text.primary} style={{ fontWeight: '500', flex: 1 }}>
        {value}
      </Text>
    </HStack>
  );
}

function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function AnalysisCard({ analysis }: Readonly<AnalysisCardProps>) {
  const { t } = useTranslation();
  const {
    keyInsights: translatedKeyInsights,
    intent: translatedIntent,
    isTranslating,
  } = useTranslateOnFly({
    keyInsights: analysis.keyInsights,
    intent: analysis.intent,
  });
  const hasMeaningfulData =
    analysis.score !== undefined ||
    analysis.sentiment ||
    analysis.intent ||
    analysis.companySize ||
    analysis.industry ||
    (analysis.keyInsights && analysis.keyInsights.length > 0);

  if (!hasMeaningfulData) return null;

  const SENTIMENT_COLOR_MAP: Record<string, string> = {
    positive: colors.status.success.main,
    negative: colors.status.error.main,
  };
  const sentimentColor =
    SENTIMENT_COLOR_MAP[analysis.sentiment ?? ''] ?? colors.status.warning.main;

  return (
    <Card padding="lg" radius="xl">
      <VStack spacing="md">
        <HStack spacing="sm" align="center">
          <Brain size={18} color={colors.accent.purple[500]} strokeWidth={2} />
          <Text variant="h6" color={colors.text.primary} style={{ fontWeight: '600' }}>
            {t('leads.analysis.aiAnalysis')}
          </Text>
        </HStack>

        {analysis.score !== undefined && <ScoreGauge score={analysis.score} />}

        <VStack spacing="sm">
          {analysis.sentiment && (
            <InfoRow
              icon={<TrendingUp size={16} color={sentimentColor} strokeWidth={2} />}
              ring={withAlpha(sentimentColor, 0.4)}
              label={t('leads.analysis.sentiment')}
              value={t(`leads.enrichment.sentiment${capitalize(analysis.sentiment)}`)}
            />
          )}
          {analysis.intent && (
            <InfoRow
              icon={<Target size={16} color={colors.primary[400]} strokeWidth={2} />}
              ring={colors.primary.borderActive}
              label={t('leads.analysis.intent')}
              value={translatedIntent ? capitalize(translatedIntent) : capitalize(analysis.intent)}
            />
          )}
          {analysis.industry && (
            <InfoRow
              icon={<Building2 size={16} color={colors.accent.blue[400]} strokeWidth={2} />}
              ring={withAlpha(colors.accent.blue[400], 0.4)}
              label={t('leads.analysis.industry')}
              value={analysis.industry}
            />
          )}
          {analysis.companySize && (
            <InfoRow
              icon={<Building2 size={16} color={colors.text.tertiary} strokeWidth={2} />}
              ring={colors.border.muted}
              label={t('leads.analysis.size')}
              value={analysis.companySize}
            />
          )}
        </VStack>

        {analysis.keyInsights && analysis.keyInsights.length > 0 && (
          <VStack spacing="sm">
            <HStack spacing="xs" align="center">
              <Lightbulb size={14} color={colors.status.warning.main} strokeWidth={2} />
              <Text variant="bodySmall" color={colors.text.secondary} style={{ fontWeight: '500' }}>
                {t('leads.analysis.keyInsights')}
              </Text>
              {isTranslating && <ActivityIndicator size={10} color={colors.text.tertiary} />}
            </HStack>
            {(translatedKeyInsights ?? analysis.keyInsights).map((insight, index) => (
              <HStack key={`insight-${index}`} spacing="sm" align="start">
                <Box
                  width={4}
                  height={4}
                  borderRadius="full"
                  backgroundColor={colors.primary[500]}
                  style={{ marginTop: 6 }}
                />
                <Text variant="caption" color={colors.text.secondary} style={{ flex: 1 }}>
                  {insight}
                </Text>
              </HStack>
            ))}
          </VStack>
        )}
      </VStack>
    </Card>
  );
}
